<template>
  <div class="qaqc-unified-dashboard">
    <!-- Summary cards row -->
    <div class="qaqc-overview-cards-row">
      <SummaryCards :stats="summaryStats" />
    </div>

    <!-- Middle row: QC Status Overview chart + QC Status panel -->
    <div class="qaqc-overview-middle-row">
      <div class="qaqc-overview-chart-col qaqc-overview-chart-col--split">
        <QCStatusOverviewChart
          v-for="panel in qcStatusChartPanels"
          :key="panel.title"
          :title="panel.title"
          :data="panel.data"
        />
      </div>
      <div class="qaqc-overview-status-col">
        <QCStatusPanel
          :panel="qcStatusPanelNormalized"
          :sample-ids="sampleIds"
          :selected-sample-ids="selectedSampleIds"
          @update:selected-sample-ids="$emit('update:selectedSampleIds', $event)"
        />
      </div>
    </div>

    <!-- Tabs: Oil CCE Tests | Gas CCE Tests | DL Tests | Separator Test -->
    <div class="qaqc-overview-tabs-wrap qaqc-overview-tabs-wrap--with-viewby">
      <QAQCTestTabs
        :model-value="activeTestTab"
        @update:model-value="$emit('update:activeTestTab', $event)"
      />
      <!-- <div class="qaqc-overview-viewby">
        <span class="qaqc-overview-viewby-label">View by:</span>
        <v-select
          v-model="groupBy"
          :items="[
            { title: 'Well', value: 'well' },
            { title: 'Report No.', value: 'report' },
          ]"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          class="qaqc-overview-viewby-select"
        ></v-select>
      </div> -->
    </div>

    <!-- Tab content: CCE, DL, or Separator view -->
    <QAQCOverviewShimmer
      v-if="tabLoading"
      tab-only
      :show-distribution="activeTestTab === 'oilCce' || activeTestTab === 'gasCce'"
    />
    <CCETestView
      v-else-if="activeTestTab === 'oilCce' || activeTestTab === 'gasCce'"
      :key="activeTestTab"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :dataset-label="activeTestTab === 'oilCce' ? 'CCE Dataset (Oil)' : 'CCE Dataset (Gas)'"
      :group-by="groupBy"
    />
    <DLTestView
      v-else-if="!tabLoading && isDlLikeTab"
      :key="activeTestTab"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :dataset-label="dlLikeDatasetLabel"
      :group-by="groupBy"
      :consistency-cross-test-rows="consistencyCrossTestRows"
      :consistency-ordering-rows="consistencyOrderingRows"
      :equilibrium-plot-rows="equilibriumPlotRows"
      :distribution-plot-options="distributionPlotOptions"
      :selected-plot-id="selectedPlotId"
      :distribution-chip-nav-context="dlLikeNavContext"
      :qc-distribution-test-name="dlLikeTestName"
      :distribution-data="distributionData"
      :matrix-test-context="dlLikeNavContext"
      :dl-material-balance-plot-options="dlMaterialBalancePlotOptions"
      :selected-material-balance-plot-id="selectedMaterialBalancePlotId"
      :dl-material-balance-distribution-data="dlMaterialBalanceDistributionData"
      :dl-feed-deviation-data="dlFeedDeviationData"
      :feed-deviation-note="dlLikeFeedDeviationNote"
      :material-balance-csv-file-name="dlLikeMaterialBalanceCsv"
      :feed-deviation-csv-file-name="dlLikeFeedDeviationCsv"
      @update:selected-plot-id="$emit('update:selectedPlotId', $event)"
      @update:selected-material-balance-plot-id="$emit('update:selectedMaterialBalancePlotId', $event)"
    />

    <!-- Distribution plots (CCE tabs only; DL embeds plots inside DLTestView) -->
    <div
      v-if="!tabLoading && (activeTestTab === 'oilCce' || activeTestTab === 'gasCce')"
      class="qaqc-overview-distribution-section"
    >
      <DistributionPlotsSection
        :key="activeTestTab"
        :plot-options="distributionPlotOptions"
        :selected-plot-id="selectedPlotId"
        :distribution-data="distributionData"
        :distribution-chip-nav-context="cceDistributionChipNavContext"
        @update:selected-plot-id="$emit('update:selectedPlotId', $event)"
      />
    </div>
  </div>
</template>

<script>
import {
  isDlLikeOverviewTab,
  QAQC_OVERVIEW_TAB_SEPARATOR,
} from "./qaqcOverviewTab.js";
import SummaryCards from "./SummaryCards.vue";
import QCStatusOverviewChart from "./QCStatusOverviewChart.vue";
import QCStatusPanel from "./QCStatusPanel.vue";
import QAQCTestTabs from "./QAQCTestTabs.vue";
import CCETestView from "./CCETestView.vue";
import DLTestView from "./DLTestView.vue";
import DistributionPlotsSection from "./DistributionPlotsSection.vue";
import QAQCOverviewShimmer from "./QAQCOverviewShimmer.vue";

export default {
  name: "UnifiedQAQCDashboard",
  components: {
    SummaryCards,
    QCStatusOverviewChart,
    QCStatusPanel,
    QAQCTestTabs,
    CCETestView,
    DLTestView,
    DistributionPlotsSection,
    QAQCOverviewShimmer,
  },
  data() {
    return {
      groupBy: "well",
    };
  },
  props: {
    summaryStats: { type: Object, required: true },
    qcStatusOverview: { type: Object, required: true },
    qcStatusPanel: { type: Object, required: true },
    activeTestTab: { type: String, default: "oilCce" },
    tabLoading: { type: Boolean, default: false },
    scorecardRows: { type: Array, default: () => [] },
    scorecardSummary: { type: Object, default: () => ({}) },
    validationMatrixRows: { type: Array, default: () => [] },
    validationMatrixTitle: { type: String, default: "" },
    validationMatrixDescription: { type: String, default: "" },
    distributionPlotOptions: { type: Array, default: () => [] },
    selectedPlotId: { type: String, default: "" },
    distributionData: { type: Object, default: () => ({}) },
    sampleIds: { type: Array, default: () => [] },
    selectedSampleIds: { type: Array, default: () => [] },
    consistencyCrossTestRows: { type: Array, default: () => [] },
    consistencyOrderingRows: { type: Array, default: () => [] },
    equilibriumPlotRows: { type: Array, default: () => [] },
    dlMaterialBalancePlotOptions: { type: Array, default: () => [] },
    selectedMaterialBalancePlotId: { type: String, default: "" },
    dlMaterialBalanceDistributionData: { type: Object, default: () => ({}) },
    dlFeedDeviationData: { type: Object, default: () => ({}) },
  },
  emits: [
    "update:activeTestTab",
    "update:selectedPlotId",
    "update:selectedMaterialBalancePlotId",
    "update:selectedSampleIds",
  ],
  computed: {
    qcStatusChartPanels() {
      const categories = this.qcStatusOverview?.categories || [];
      const series = this.qcStatusOverview?.series || [];
      return series.map((s) => ({
        title: s.name,
        data: { categories, values: s.values || [] },
      }));
    },
    isDlLikeTab() {
      return isDlLikeOverviewTab(this.activeTestTab);
    },
    isSeparatorTab() {
      return this.activeTestTab === QAQC_OVERVIEW_TAB_SEPARATOR;
    },
    dlLikeDatasetLabel() {
      return this.isSeparatorTab ? "Separator Dataset" : "DL Dataset";
    },
    dlLikeNavContext() {
      return this.isSeparatorTab ? "separator" : "dl";
    },
    dlLikeTestName() {
      return this.isSeparatorTab ? "separatortests" : "dltests";
    },
    dlLikeMaterialBalanceCsv() {
      return this.isSeparatorTab
        ? "separator_material_balance_distribution.csv"
        : "dl_material_balance_distribution.csv";
    },
    dlLikeFeedDeviationCsv() {
      return this.isSeparatorTab
        ? "separator_overall_feed_deviation.csv"
        : "dl_overall_feed_deviation.csv";
    },
    dlLikeFeedDeviationNote() {
      const source = this.isSeparatorTab ? "separator" : "DL";
      return `The deviation corresponds to the abs rel. error (%) between the reported original composition and the composition reconstructed from ${source} vapor-phase data via back-calculation of liquid moles.`;
    },
    cceDistributionChipNavContext() {
      return this.activeTestTab === "gasCce" ? "gas" : "oil";
    },
    qcStatusPanelNormalized() {
      const p = this.qcStatusPanel;
      const fromApi = this.parseStatNum(this.summaryStats?.totalTests);
      const panelTotal = this.parseStatNum(p?.passed?.total);
      const total =
        fromApi !== null && fromApi > 0
          ? fromApi
          : panelTotal !== null && panelTotal > 0
            ? panelTotal
            : null;
      if (total === null || total <= 0) return p;
      const fix = (block) => {
        const cnt = this.parseStatNum(block?.count);
        const percent =
          cnt !== null ? `${Math.round((cnt / total) * 100)}%` : block.percent;
        return {
          ...block,
          total: String(total),
          percent,
        };
      };
      return {
        ...p,
        passed: fix(p.passed),
        needsReview: fix(p.needsReview),
        failed: fix(p.failed),
      };
    },
  },
  methods: {
    parseStatNum(v) {
      if (v === undefined || v === null || v === "") return null;
      if (typeof v === "number" && !Number.isNaN(v)) return v;
      const s = String(v).trim();
      if (s === "-") return null;
      const n = Number(s.replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? null : n;
    },
  },
};
</script>
